import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Send, Check, Loader2 } from "lucide-react";
import { WhatsAppCTA } from "../WhatsAppCTA";
import { CONTACT, MAP_EMBED } from "../../lib/site";
import { fadeUp, viewport } from "../../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const inputClass =
    "w-full bg-[#050505] border border-white/[0.08] rounded-2xl px-5 py-3.5 text-[15px] text-[#f5f5f7] placeholder:text-[#86868b] focus:outline-none focus:border-white/25 transition-colors";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <input
        type="text"
        placeholder="Seu nome"
        value={form.name}
        onChange={update("name")}
        required
        data-testid="contact-name"
        className={inputClass}
      />
      <input
        type="tel"
        placeholder="Seu telefone / WhatsApp"
        value={form.phone}
        onChange={update("phone")}
        required
        data-testid="contact-phone"
        className={inputClass}
      />
      <textarea
        placeholder="Conte o que aconteceu com seu aparelho"
        value={form.message}
        onChange={update("message")}
        required
        rows={4}
        data-testid="contact-message"
        className={`${inputClass} resize-none`}
      />
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="contact-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#f5f5f7] text-[#050505] py-3.5 text-[15px] font-medium hover:bg-white transition-colors disabled:opacity-60"
      >
        {status === "loading" && <Loader2 size={18} className="animate-spin" />}
        {status === "success" ? (
          <>
            <Check size={18} /> Mensagem enviada
          </>
        ) : (
          <>
            <Send size={16} /> Enviar mensagem
          </>
        )}
      </button>
      {status === "success" && (
        <p data-testid="contact-success" className="text-[13px] text-[#86868b] text-center">
          Recebemos sua mensagem. Em breve entraremos em contato.
        </p>
      )}
      {status === "error" && (
        <p data-testid="contact-error" className="text-[13px] text-red-400/80 text-center">
          Não foi possível enviar. Tente pelo WhatsApp.
        </p>
      )}
    </form>
  );
};

export const Location = () => {
  return (
    <section
      id="contato"
      data-testid="location-section"
      className="relative bg-[#050505] py-28 md:py-40 border-t border-white/[0.05]"
    >
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="text-4xl md:text-6xl font-bold tracking-tighter text-[#f5f5f7] text-center"
        >
          Nos encontre.
        </motion.h2>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {/* Map + address card */}
          <div className="bg-[#1a1a1a] border border-white/[0.05] rounded-[32px] overflow-hidden flex flex-col">
            <div className="relative h-[280px] md:h-[340px]">
              <iframe
                title="Localização Inove Tech"
                src={MAP_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="map-dark w-full h-full"
                data-testid="location-map"
              />
            </div>
            <div className="p-8">
              <div className="flex items-start gap-3">
                <MapPin size={22} className="text-[#f5f5f7] shrink-0 mt-1" />
                <div className="text-[15px] leading-relaxed text-[#86868b]">
                  {CONTACT.addressLines.map((line, i) => (
                    <span key={i} className="block">
                      {line}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-8">
                <WhatsAppCTA label="Falar no WhatsApp" testId="location-whatsapp" />
              </div>
            </div>
          </div>

          {/* Contact form card */}
          <div className="bg-[#1a1a1a] border border-white/[0.05] rounded-[32px] p-8 md:p-10">
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#f5f5f7]">
              Prefere deixar uma mensagem?
            </h3>
            <p className="mt-3 mb-8 text-[15px] text-[#86868b]">
              Sem formulário obrigatório — mas se quiser, conte seu caso por aqui
              e retornamos o contato.
            </p>
            <ContactForm />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
